import { Popover, Transition } from '@headlessui/react'
import { format, parseISO, sub, subDays } from 'date-fns'
import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import '../../../assets/styles/checkbox.css'
import NoIndexGraph from './no-index-graph'
import TabButton from './tab-button'
import Flag from '../../../assets/images/icons/flag.svg?react'
import ButtonDownPrimary from '../../../assets/images/icons/primary-chevron-down.svg?react'
import SearchIcon from '../../../assets/images/icons/search-lg.svg?react'
import ChartTooltip from '../../../shared/components/ChartTooltip'
import {
  getPersistentColorsBySiteId,
  orderSitesForDisplay,
} from '../../../shared/theme/chartColors'
import { findNiceAxis } from '../../../shared/theme/niceAxis'
import type {
  HistoricalIndexRequest,
  HistoricalIndexResponse,
} from '../../../store/types/dashboard'
import DateRangePicker from '../../../utils/date-pickers/range-picker'
import ManageTableCheckbox from '../../../utils/forms/checkbox/manage-table-checkbox'
import SpinLoader from '../../../utils/loaders/spin'

export type HistoricalIndexSite = {
  site_id: number
  url: string
  siteName?: string
  is_mine?: boolean
  has_data?: boolean
  // Report pages pass the raw site breakdown, where `url` is the bare
  // domain. The chart endpoints key series by the site's display name,
  // which for channel-based/hybrid panels is `${url} - ${seller_name}`.
  seller_name?: string
}

export type HistoricalIndexChartData = Partial<HistoricalIndexResponse>

type Row = {
  time: string
  [siteId: string]: number | string | null | undefined
}

// Every key a chart series for this site may come back under: the
// display name with the seller appended (channel-based/hybrid panels) or
// the bare url (everything else, and the dashboard's sites endpoint, whose
// `url` already is the display name).
function getSeriesKeys(site: HistoricalIndexSite): string[] {
  return site.seller_name
    ? [`${site.url} - ${site.seller_name}`, site.url]
    : [site.url]
}

export function buildRows(
  chartData: HistoricalIndexChartData | undefined,
  sites: HistoricalIndexSite[],
): Row[] {
  if (!chartData?.dates || !chartData?.chart) return []
  const siteIdByKey = new Map<string, number>()
  sites.forEach((site) =>
    getSeriesKeys(site).forEach((key) => {
      if (!siteIdByKey.has(key)) siteIdByKey.set(key, site.site_id)
    }),
  )
  return chartData.dates.map((time, dateIndex) => {
    const row: Row = { time }
    Object.entries(chartData.chart!).forEach(([key, values]) => {
      const siteId = siteIdByKey.get(key)
      if (siteId != null) row[siteId] = values[dateIndex]
    })
    return row
  })
}

export function LoadingHistoricalIndexChart() {
  return (
    <div className="sm:pb-16 pb-4">
      <div className="flex  flex-col sm:col-span-3 border-0 shadow">
        <div className="flex flex-col border-b border-solid border-gray-200 container mx-auto p-6">
          <div className="flex flex-col">
            <div className="flex flex-col sm:flex-row sm:justify-between">
              <h1 className="font-medium text-lg text-gray-900 pb-4 sm:pb-0">
                Historical Index Chart
              </h1>
              <div className="flex flex-row pb-5">
                <button
                  disabled
                  className="border border-solid border-gray-200 rounded-lg shadow-sm h-10"
                >
                  <span className="px-4 font-medium text-sm text-gray-300">
                    Choose custom dates
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col container mx-auto ">
            <div className="flex justify-center items-center">
              <div className="flex flex-col items-center">
                <SpinLoader />
                <div className="font-medium text-sm text-gray-700 pb-1 pt-4">
                  Calculating...
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

type HistoricalIndexWidgetProps = {
  sites: HistoricalIndexSite[] | undefined
  isSitesLoaded: boolean
  chartData: HistoricalIndexChartData | undefined
  isLoading: boolean
  // Distinct from isLoading: true for every in-flight fetch, including a
  // refetch triggered by switching tabs/date range after data already
  // loaded once — isLoading alone only covers the very first fetch.
  isFetching?: boolean
  isSuccess: boolean
  onFilterChange: (filter: {
    siteIds: number[]
    startAt: string
    endAt: string
  }) => void
  onTabClick?: (event: string) => void
}

export default function HistoricalIndexWidget({
  sites,
  isSitesLoaded,
  chartData,
  isLoading,
  isFetching = false,
  isSuccess,
  onFilterChange,
  onTabClick,
}: HistoricalIndexWidgetProps) {
  const [isVisible, setIsVisible] = useState<number[]>([])
  const [competitorSearch, setCompetitorSearch] = useState('')

  const [filter, setFilter] = useState<
    HistoricalIndexRequest & { key: string }
  >({
    key: '7days',
    startAt: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endAt: format(new Date(), 'yyyy-MM-dd'),
  })

  const coloredSites = useMemo(() => {
    const ordered = orderSitesForDisplay(sites ?? [])
    const colors = getPersistentColorsBySiteId(
      ordered.map((site) => site.site_id),
    )
    return ordered.map((site) => ({
      ...site,
      color: colors.get(site.site_id)!,
    }))
  }, [sites])

  useEffect(() => {
    if (!isSitesLoaded) return
    // Prefer sites that actually have index history for the default
    // selection, so the chart doesn't open with blank lines for
    // newly-tracked competitors — fall back to sites without data only if
    // there aren't enough with data to fill the default count.
    const withData = coloredSites.filter((site) => site.has_data)
    const withoutData = coloredSites.filter((site) => !site.has_data)
    const defaultSites = [...withData, ...withoutData].slice(0, 5)
    setIsVisible(defaultSites.map((site) => site.site_id))
  }, [isSitesLoaded, coloredSites])

  useEffect(() => {
    // The default-selection effect above sets isVisible in the same commit
    // isSitesLoaded flips to true, but effects still run in declaration
    // order within that commit — without this guard we'd fire once with
    // the stale empty isVisible and again immediately after with the real
    // selection.
    if (!isSitesLoaded || !isVisible.length) return
    onFilterChange({
      siteIds: isVisible,
      startAt: filter.startAt,
      endAt: filter.endAt,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSitesLoaded, isVisible, filter])

  const rows = useMemo(
    () => buildRows(chartData, coloredSites),
    [chartData, coloredSites],
  )

  const TICK_COUNT = 6
  const { yDomain, yTicks } = useMemo((): {
    yDomain: [number | string, number | string]
    yTicks: number[] | undefined
  } => {
    const values = rows.flatMap((row) =>
      Object.keys(row)
        .filter((key) => key !== 'time')
        .map((key) => row[key])
        .filter((value): value is number => typeof value === 'number'),
    )
    if (!values.length) return { yDomain: ['auto', 'auto'], yTicks: undefined }
    const min = Math.min(...values)
    const max = Math.max(...values)
    const { niceMin, niceMax, ticks } = findNiceAxis(min, max, TICK_COUNT)
    return { yDomain: [niceMin, niceMax], yTicks: ticks }
  }, [rows])

  const visibleSites = coloredSites.filter((site) =>
    isVisible.includes(site.site_id),
  )

  const searchedSites = useMemo(() => {
    const query = competitorSearch.trim().toLowerCase()
    if (!query) return coloredSites
    return coloredSites.filter((site) =>
      (site.siteName ?? site.url).toLowerCase().includes(query),
    )
  }, [coloredSites, competitorSearch])

  const setPresetDateFilter = (days: number, unit: 'days' | 'months') => {
    setFilter({
      key: `${days}${unit}`,
      startAt: format(sub(new Date(), { [unit]: days }), 'yyyy-MM-dd'),
      endAt: format(new Date(), 'yyyy-MM-dd'),
    })
  }

  if (!chartData) {
    return <LoadingHistoricalIndexChart />
  }
  if (!isLoading && !isSuccess) {
    return <NoIndexGraph />
  }

  return (
    <div className="sm:pb-16 pb-4">
      <div className="flex  flex-col sm:col-span-3 sm:border sm:border-solid sm:border-gray-200 sm:rounded-lg sm:shadow">
        <div className="flex flex-col lg:border-b border-solid border-gray-200 container mx-auto py-6 lg:p-6">
          <div className="pb-6 flex flex-col">
            <div className="flex flex-col sm:flex-row sm:justify-between">
              <h1 className="font-medium text-lg text-gray-900 pb-4 sm:pb-0">
                Historical Index Chart
              </h1>
              <div className="flex flex-row pb-5">
                <div className="pr-3">
                  <Popover className="relative">
                    <Popover.Button className="flex flex-row items-center justify-center h-10 px-3 lg:px-4 py-2.5 border border-solid border-gray-300 rounded-lg shadow-sm">
                      <span className="font-medium text-sm text-gray-700 whitespace-nowrap">
                        Competitors ({visibleSites.length}/{coloredSites.length}
                        )
                      </span>
                      <ButtonDownPrimary className="ml-2 shrink-0" />
                    </Popover.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                      afterLeave={() => setCompetitorSearch('')}
                    >
                      <Popover.Panel className="absolute left-0 sm:right-0 sm:left-auto z-[999] mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg p-3">
                        <div className="relative mb-2">
                          <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                            <SearchIcon className="text-gray-400" />
                          </span>
                          <input
                            type="text"
                            value={competitorSearch}
                            onChange={(e) =>
                              setCompetitorSearch(e.target.value)
                            }
                            placeholder="Search competitors"
                            className="w-full text-sm border border-gray-300 rounded-lg py-1.5 pl-8 pr-3 focus:ring-0 !outline-none"
                          />
                        </div>
                        <div className="flex items-center pb-2 mb-2 border-b border-gray-200">
                          <ManageTableCheckbox
                            isChecked={
                              visibleSites.length === coloredSites.length &&
                              coloredSites.length > 0
                            }
                            handleCheckboxChange={() => {
                              setIsVisible(
                                visibleSites.length === coloredSites.length
                                  ? []
                                  : coloredSites.map((site) => site.site_id),
                              )
                            }}
                          />
                          <label className="text-sm font-medium text-gray-700">
                            Select all ({visibleSites.length}/
                            {coloredSites.length})
                          </label>
                        </div>
                        <div className="flex flex-col space-y-2 max-h-72 overflow-y-auto">
                          {searchedSites.length ? (
                            searchedSites.map((site) => (
                              <div
                                key={site.site_id}
                                className="flex flex-row items-center"
                              >
                                <ManageTableCheckbox
                                  isChecked={isVisible.includes(site.site_id)}
                                  handleCheckboxChange={() => {
                                    setIsVisible((current) =>
                                      current.includes(site.site_id)
                                        ? current.filter(
                                            (id) => id !== site.site_id,
                                          )
                                        : [...current, site.site_id],
                                    )
                                  }}
                                />
                                <div
                                  className="w-3 h-3 rounded-full mr-1.5 shrink-0 ring-1 ring-black/5"
                                  style={{ backgroundColor: site.color }}
                                />
                                <span className="font-normal text-xs text-gray-500 pl-0.5 break-words">
                                  {site.siteName ?? site.url}
                                </span>
                                {site.is_mine && (
                                  <Flag className="ml-1 shrink-0" />
                                )}
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-gray-400 py-2">
                              {competitorSearch
                                ? 'No competitors match your search'
                                : 'No competitors with data'}
                            </p>
                          )}
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </Popover>
                </div>
                <DateRangePicker
                  onChange={([startAt, endAt]: [Date, Date]) => {
                    setFilter({
                      startAt: format(startAt, 'yyyy-MM-dd'),
                      endAt: format(endAt, 'yyyy-MM-dd'),
                      key: 'custom',
                    })
                  }}
                  filter={filter}
                />
              </div>
            </div>
            <div className="flex flex-row justify-between sm:justify-start border-b sm:border-0 pb-2">
              <TabButton
                value={'7days'}
                currentValue={filter.key}
                onClick={() => {
                  setPresetDateFilter(7, 'days')
                  onTabClick?.('7 Days Button Clicked')
                }}
              >
                7 days
              </TabButton>
              <TabButton
                value={'30days'}
                currentValue={filter.key}
                onClick={() => {
                  setPresetDateFilter(30, 'days')
                  onTabClick?.('30 Days Button Clicked')
                }}
              >
                30 days
              </TabButton>
              <TabButton
                value={'2months'}
                currentValue={filter.key}
                onClick={() => {
                  setPresetDateFilter(2, 'months')
                  onTabClick?.('2 Months Button Clicked')
                }}
              >
                2 Months
              </TabButton>
            </div>
          </div>
          <div className="w-full relative min-h-[450px]">
            {(isLoading || isFetching) && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white-100/70">
                <SpinLoader className="!m-0 !bg-transparent" />
                <div className="font-medium text-sm text-gray-700 pb-1 pt-4">
                  Loading...
                </div>
              </div>
            )}
            <ResponsiveContainer width="100%" height={450}>
              <LineChart
                data={rows}
                margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke="#D0D5DD"
                  vertical={false}
                />
                <XAxis
                  dataKey="time"
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={40}
                  tick={{ fill: '#667085', fontSize: 12, dy: 8 }}
                  tickFormatter={(value: string) =>
                    format(parseISO(value), 'MMM d yyyy')
                  }
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#667085', fontSize: 12 }}
                  domain={yDomain}
                  ticks={yTicks}
                  width={48}
                  label={{
                    value: 'Index',
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      fill: '#667085',
                      fontSize: 12,
                      textAnchor: 'middle',
                    },
                  }}
                />
                <RechartsTooltip
                  content={(props) => (
                    <ChartTooltip
                      {...props}
                      label={
                        typeof props.label === 'string' && props.label
                          ? format(parseISO(props.label), 'MMM d, h a')
                          : props.label
                      }
                    />
                  )}
                  cursor={{ stroke: '#EAECF0' }}
                  wrapperStyle={{ opacity: 1, zIndex: 50 }}
                />
                {visibleSites.map((site) => (
                  <Line
                    key={site.site_id}
                    type="monotone"
                    dataKey={site.site_id}
                    name={site.siteName ?? site.url}
                    stroke={site.color}
                    connectNulls
                    isAnimationActive={false}
                    dot={false}
                    activeDot={{ r: 4, fill: site.color, stroke: site.color }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}
